/**
 * Leads Tab - Lead Management Table View
 * Provides a comprehensive table-based view for managing leads
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tooltip,
  LinearProgress,
  Alert,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Popover,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  TrendingUp as ScoreIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  CheckCircle as ConvertIcon,
  NoteAdd as NoteAddIcon,
  Assignment as TaskIcon,
} from '@mui/icons-material';
import { MobileDataList, MobileActionSheet } from '../../../components/mobile';
import { format } from 'date-fns';
import { useLeads } from '../hooks/useAICRM';
import { useDeleteLead, useUpdateLeadStatus, useUpdateLeadPriority, useMarkEmailVerified, useConvertLead, useScoreLead } from '../hooks/useLeadMutations';
import { Lead, LeadFilters } from '../types';
import LeadForm, { LeadFormData } from '../components/LeadForm';
import LeadDetailDrawer, { Lead as DrawerLead } from '../components/LeadDetailDrawer';
import ComposeEmailDialog from '../components/ComposeEmailDialog';
import { useLeadActivities } from '../hooks/useAICRM';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateLead, useUpdateLead, useAddLeadActivity } from '../hooks/useLeadMutations';
import { useTaskAssignees, useAssignLead, useCreateTask } from '../hooks/useTasks';
import { showToast } from '../utils/toast';
import { AddNoteFormData } from '../components/AddNoteForm';
import AttributeSelectionDialog from '../components/AttributeSelectionDialog';
import PermissionGuard from '../../../components/guards/PermissionGuard';

// Status configuration
const statusConfig: Record<string, { label: string; color: 'default' | 'info' | 'primary' | 'warning' | 'success' | 'error' }> = {
  NEW: { label: 'New', color: 'info' },
  CONTACTED: { label: 'Contacted', color: 'primary' },
  QUALIFIED: { label: 'Qualified', color: 'success' },
  UNQUALIFIED: { label: 'Unqualified', color: 'warning' },
  CONVERTED: { label: 'Converted', color: 'success' },
  LOST: { label: 'Lost', color: 'error' },
};

// Priority configuration
const priorityConfig: Record<string, { label: string; color: 'default' | 'info' | 'warning' | 'error' }> = {
  LOW: { label: 'Low', color: 'default' },
  MEDIUM: { label: 'Medium', color: 'info' },
  HIGH: { label: 'High', color: 'warning' },
  URGENT: { label: 'Urgent', color: 'error' },
};

// Source labels
const sourceLabels: Record<string, string> = {
  WEB_FORM: 'Web Form',
  REFERRAL: 'Referral',
  EVENT: 'Event',
  COLD_CALL: 'Cold Call',
  SOCIAL_MEDIA: 'Social Media',
  PARTNER: 'Partner',
  OTHER: 'Other',
};

type SortField = 'leadNo' | 'contactName' | 'companyName' | 'status' | 'priority' | 'aiScore' | 'createdAt';
type SortOrder = 'asc' | 'desc';

// Transform Lead from API type to Drawer type
const transformLeadForDrawer = (lead: Lead): DrawerLead => ({
  id: lead.id,
  leadNumber: lead.leadNo,
  name: lead.contactName,
  email: lead.email,
  emailVerified: lead.emailVerified,
  phone: lead.phone,
  companyName: lead.companyName,
  position: lead.position,
  source: lead.source,
  status: lead.status,
  priority: lead.priority,
  aiScore: lead.aiScore || 0,
  scoringFactors: lead.aiScoreReason as DrawerLead['scoringFactors'],
  estimatedBudget: lead.estimatedBudget,
  lastContactedAt: lead.lastContactedAt,
  industry: lead.industry,
  location: lead.location,
  assignedUser: lead.assignedTo,
  notes: lead.notes,
  createdAt: lead.createdAt,
  updatedAt: lead.updatedAt,
});

interface LeadsTabProps {
  initialMinScore?: number;
}

const LeadsTab: React.FC<LeadsTabProps> = ({ initialMinScore }) => {
  const queryClient = useQueryClient();
  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 10 : 10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<LeadFilters>(
    initialMinScore ? { minScore: initialMinScore } : {}
  );
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [leadFormMode, setLeadFormMode] = useState<'create' | 'edit'>('create');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuLead, setMenuLead] = useState<Lead | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [attrDialogOpen, setAttrDialogOpen] = useState(false);
  const [pendingLeadData, setPendingLeadData] = useState<LeadFormData | null>(null);
  const [attrDialogLoading, setAttrDialogLoading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState<{ name?: string; email?: string; company?: string; leadId?: string } | null>(null);
  const [notePopoverAnchor, setNotePopoverAnchor] = useState<HTMLElement | null>(null);
  const [notePopoverLead, setNotePopoverLead] = useState<Lead | null>(null);
  const [quickNoteText, setQuickNoteText] = useState('');
  const [quickTaskDialogOpen, setQuickTaskDialogOpen] = useState(false);
  const [quickTaskLead, setQuickTaskLead] = useState<Lead | null>(null);
  const [quickTaskForm, setQuickTaskForm] = useState({
    title: '',
    taskType: 'FOLLOW_UP',
    priority: 'MEDIUM',
    dueDate: '',
    assignedToUserId: '',
  });

  // Build API filters
  const apiFilters = {
    ...filters,
    search: searchQuery || undefined,
    sortField,
    sortOrder,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  };

  // Queries
  const { data: leadsData, isLoading, isError, error, refetch } = useLeads(apiFilters);
  const { data: leadActivities } = useLeadActivities(selectedLead?.id || '');
  const { data: assignees } = useTaskAssignees();

  // Mutations
  const createLead = useCreateLead();
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();
  const createTask = useCreateTask();
  const updateLeadStatus = useUpdateLeadStatus();
  const updateLeadPriority = useUpdateLeadPriority();
  const markEmailVerified = useMarkEmailVerified();
  const addLeadActivity = useAddLeadActivity();
  const assignLead = useAssignLead();
  const convertLead = useConvertLead();
  const scoreLead = useScoreLead();

  // Data
  const leads = leadsData?.data || [];
  const totalLeads = leadsData?.pagination?.total || 0;

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setDetailDrawerOpen(true);
    handleCloseMenu();
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    setLeadFormMode('edit');
    setLeadFormOpen(true);
    handleCloseMenu();
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setLeadFormMode('create');
    setLeadFormOpen(true);
  };

  const handleDeleteLead = async (leadId: string) => {
    const lead = leads.find(l => l.id === leadId);
    if (!window.confirm(`Are you sure you want to delete lead "${lead?.contactName || 'this lead'}"?`)) {
      return;
    }
    try {
      await deleteLead.mutateAsync(leadId);
      showToast.leadDeleted();
      setDetailDrawerOpen(false);
      setSelectedLead(null);
    } catch (err) {
      showToast.operationFailed('Failed to delete lead');
    }
    handleCloseMenu();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, lead: Lead) => {
    setAnchorEl(event.currentTarget);
    setMenuLead(lead);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuLead(null);
  };

  const handleLeadFormSubmit = async (data: LeadFormData) => {
    try {
      if (leadFormMode === 'create') {
        // Show attribute selection dialog before creating
        setPendingLeadData(data);
        setLeadFormOpen(false);
        setAttrDialogOpen(true);
        return;
      } else if (selectedLead) {
        await updateLead.mutateAsync({ id: selectedLead.id, ...data });
        showToast.leadUpdated();
      }
      setLeadFormOpen(false);
    } catch (err) {
      showToast.operationFailed('Failed to save lead');
      throw err;
    }
  };

  const handleConfirmCreateLead = async (attributes: Record<string, string>) => {
    if (!pendingLeadData) return;
    setAttrDialogLoading(true);
    try {
      await createLead.mutateAsync({
        ...pendingLeadData,
        customAttributes: Object.keys(attributes).length > 0 ? attributes : undefined,
      } as any);
      showToast.leadCreated();
      setAttrDialogOpen(false);
      setPendingLeadData(null);
    } catch (err) {
      showToast.operationFailed('Failed to create lead');
    } finally {
      setAttrDialogLoading(false);
    }
  };

  const handleUpdateStatus = async (leadId: string, newStatus: string) => {
    try {
      await updateLeadStatus.mutateAsync({ leadId, status: newStatus });
      showToast.statusUpdated();

      // Update selectedLead with the new status
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({
          ...selectedLead,
          status: newStatus
        });
      }
    } catch (err) {
      showToast.operationFailed('Failed to update status');
    }
  };

  const handleUpdatePriority = async (leadId: string, newPriority: string) => {
    try {
      await updateLeadPriority.mutateAsync({ leadId, priority: newPriority });
      showToast.priorityUpdated();

      // Update selectedLead with the new priority
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead({
          ...selectedLead,
          priority: newPriority
        });
      }
    } catch (err) {
      showToast.operationFailed('Failed to update priority');
    }
  };

  const handleVerifyEmail = async (lead: DrawerLead) => {
    try {
      await markEmailVerified.mutateAsync(lead.id);
      showToast.success('Email marked as verified');
      // Update selectedLead so the drawer reflects the change immediately
      if (selectedLead && selectedLead.id === lead.id) {
        setSelectedLead({ ...selectedLead, emailVerified: true } as any);
      }
    } catch (err) {
      showToast.operationFailed('Failed to verify email');
    }
  };

  const handleAddActivity = async (leadId: string, activity: AddNoteFormData) => {
    try {
      await addLeadActivity.mutateAsync({ leadId, activity });
      showToast.activityAdded();
    } catch (err) {
      showToast.operationFailed('Failed to add activity');
      throw err;
    }
  };

  const handleAssignLead = async (leadId: string, assignedToUserId: string) => {
    try {
      await assignLead.mutateAsync({ leadId, assignedToUserId });
      showToast.success('Lead assigned successfully');

      // Update selectedLead with the new assigned user info
      if (selectedLead && selectedLead.id === leadId) {
        const assignedStaff = assignees?.find(a => a.id === assignedToUserId);
        setSelectedLead({
          ...selectedLead,
          assignedToUserId: assignedToUserId,
          assignedTo: assignedStaff ? {
            id: assignedStaff.id,
            name: assignedStaff.name,
            email: assignedStaff.email || ''
          } : undefined
        });
      }

      // Refresh leads to update the list
      refetch();
    } catch (err) {
      showToast.operationFailed('Failed to assign lead');
    }
  };

  const handleQuickTaskOpen = (e: React.MouseEvent, lead: Lead) => {
    e.stopPropagation();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setQuickTaskLead(lead);
    setQuickTaskForm({
      title: `Follow up: ${lead.contactName}`,
      taskType: 'FOLLOW_UP',
      priority: 'MEDIUM',
      dueDate: tomorrow.toISOString().slice(0, 16),
      assignedToUserId: lead.assignedTo?.id || '',
    });
    setQuickTaskDialogOpen(true);
  };

  const handleQuickTaskClose = () => {
    setQuickTaskDialogOpen(false);
    setQuickTaskLead(null);
  };

  const handleQuickTaskSubmit = async () => {
    if (!quickTaskLead || !quickTaskForm.title.trim() || !quickTaskForm.assignedToUserId) return;
    try {
      await createTask.mutateAsync({
        title: quickTaskForm.title.trim(),
        taskType: quickTaskForm.taskType,
        priority: quickTaskForm.priority,
        dueDate: new Date(quickTaskForm.dueDate).toISOString(),
        assignedToUserId: quickTaskForm.assignedToUserId,
        leadId: quickTaskLead.id,
      });
      showToast.success('Task created');
      handleQuickTaskClose();
    } catch {
      showToast.operationFailed('Failed to create task');
    }
  };

  const handleQuickNoteOpen = (e: React.MouseEvent<HTMLElement>, lead: Lead) => {
    e.stopPropagation();
    setNotePopoverAnchor(e.currentTarget);
    setNotePopoverLead(lead);
    setQuickNoteText('');
  };

  const handleQuickNoteClose = () => {
    setNotePopoverAnchor(null);
    setNotePopoverLead(null);
    setQuickNoteText('');
  };

  const handleQuickNoteSubmit = async () => {
    if (!notePopoverLead || !quickNoteText.trim()) return;
    try {
      await addLeadActivity.mutateAsync({
        leadId: notePopoverLead.id,
        activity: { type: 'NOTE', content: quickNoteText.trim() },
      });
      showToast.activityAdded();
      handleQuickNoteClose();
    } catch {
      showToast.operationFailed('Failed to add note');
    }
  };

  const handleConvertLead = async (leadId: string) => {
    const lead = leads.find(l => l.id === leadId) || (selectedLead?.id === leadId ? selectedLead : null);
    if (!window.confirm(`Convert "${lead?.contactName || 'this lead'}" to a client? This will create a CRM client record.`)) {
      return;
    }
    try {
      await convertLead.mutateAsync(leadId);
      showToast.success('Lead converted to client successfully');
      // Close drawer if it was open for this lead
      if (selectedLead?.id === leadId) {
        setDetailDrawerOpen(false);
        setSelectedLead(null);
      }
      handleCloseMenu();
    } catch (err: any) {
      showToast.operationFailed(err?.response?.data?.message || 'Failed to convert lead');
    }
  };

  const handleScoreLead = async (leadId: string) => {
    try {
      const updatedLead = await scoreLead.mutateAsync(leadId);
      // Update selectedLead in place so the drawer reflects new score immediately
      if (selectedLead?.id === leadId) {
        setSelectedLead(updatedLead);
      }
      showToast.success('AI score generated successfully');
    } catch (err: any) {
      showToast.operationFailed(err?.response?.data?.message || 'Failed to score lead');
    }
  };

  // Get AI Score color
  const getScoreColor = (score?: number): 'error' | 'warning' | 'success' | 'default' => {
    if (!score) return 'default';
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  // Render functions
  const renderTableHeader = () => (
    <TableHead>
      <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', py: 1.5 } }}>
        <TableCell sx={{ width: 250 }}>
          <TableSortLabel
            active={sortField === 'contactName'}
            direction={sortField === 'contactName' ? sortOrder : 'asc'}
            onClick={() => handleSort('contactName')}
          >
            Contact
          </TableSortLabel>
        </TableCell>
        <TableCell sx={{ width: 150 }}>
          <TableSortLabel
            active={sortField === 'status'}
            direction={sortField === 'status' ? sortOrder : 'asc'}
            onClick={() => handleSort('status')}
          >
            Status
          </TableSortLabel>
        </TableCell>
        <TableCell sx={{ width: 100 }}>
          <TableSortLabel
            active={sortField === 'priority'}
            direction={sortField === 'priority' ? sortOrder : 'asc'}
            onClick={() => handleSort('priority')}
          >
            Priority
          </TableSortLabel>
        </TableCell>
        <TableCell sx={{ width: 100 }}>
          <TableSortLabel
            active={sortField === 'aiScore'}
            direction={sortField === 'aiScore' ? sortOrder : 'asc'}
            onClick={() => handleSort('aiScore')}
          >
            Score
          </TableSortLabel>
        </TableCell>
        <TableCell sx={{ width: 130 }}>Assigned</TableCell>
        <TableCell sx={{ width: 100 }}>
          <TableSortLabel
            active={sortField === 'createdAt'}
            direction={sortField === 'createdAt' ? sortOrder : 'asc'}
            onClick={() => handleSort('createdAt')}
          >
            Date
          </TableSortLabel>
        </TableCell>
        <TableCell align="right" sx={{ width: 50 }}></TableCell>
      </TableRow>
    </TableHead>
  );

  const renderTableRow = (lead: Lead) => (
    <TableRow
      key={lead.id}
      hover
      sx={{
        cursor: 'pointer',
        '& td': { py: 1.5, fontSize: '0.875rem' },
        '&:hover': { bgcolor: 'action.hover' },
      }}
      onClick={() => handleViewLead(lead)}
    >
      {/* Contact - Combined name, email, company */}
      <TableCell>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: lead.status === 'CONVERTED' ? 'success.main' :
                       lead.status === 'QUALIFIED' ? 'primary.main' :
                       lead.status === 'LOST' ? 'error.light' : 'grey.400',
              fontSize: '0.875rem',
            }}
          >
            {lead.contactName?.charAt(0) || '?'}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {lead.contactName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {lead.companyName || lead.email || '-'}
            </Typography>
          </Box>
          {/* Quick action icons - shown on row hover */}
          <Stack
            direction="row"
            spacing={0}
            onClick={(e) => e.stopPropagation()}
            sx={{
              opacity: 0,
              '.MuiTableRow-root:hover &': { opacity: 1 },
              transition: 'opacity 0.15s',
              flexShrink: 0,
            }}
          >
            <Tooltip title="Add Note">
              <IconButton size="small" onClick={(e) => handleQuickNoteOpen(e, lead)} sx={{ color: 'text.secondary' }}>
                <NoteAddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create Task">
              <IconButton size="small" onClick={(e) => handleQuickTaskOpen(e, lead)} sx={{ color: 'text.secondary' }}>
                <TaskIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {lead.phone && (
              <Tooltip title={`Call ${lead.phone}`}>
                <IconButton size="small" onClick={() => window.open(`tel:${lead.phone}`)} sx={{ color: 'text.secondary' }}>
                  <PhoneIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {lead.email && (
              <Tooltip title={`Email ${lead.email}`}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEmailRecipient({ name: lead.contactName, email: lead.email, company: lead.companyName, leadId: lead.id });
                    setEmailDialogOpen(true);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <EmailIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </TableCell>

      {/* Status */}
      <TableCell>
        <Chip
          label={statusConfig[lead.status]?.label || lead.status}
          color={statusConfig[lead.status]?.color || 'default'}
          size="small"
          sx={{ fontWeight: 500, minWidth: 80 }}
        />
      </TableCell>

      {/* Priority */}
      <TableCell>
        <Chip
          label={priorityConfig[lead.priority]?.label || lead.priority}
          color={priorityConfig[lead.priority]?.color || 'default'}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      </TableCell>

      {/* AI Score */}
      <TableCell>
        {lead.aiScore !== undefined && lead.aiScore !== null ? (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color: lead.aiScore >= 80 ? 'success.main' :
                     lead.aiScore >= 60 ? 'warning.main' : 'error.main'
            }}
          >
            {Math.round(lead.aiScore)}%
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )}
      </TableCell>

      {/* Assigned To */}
      <TableCell>
        {lead.assignedTo ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem', bgcolor: 'primary.light' }}>
              {lead.assignedTo.name?.charAt(0)}
            </Avatar>
            <Typography variant="body2" noWrap sx={{ maxWidth: 80 }}>
              {lead.assignedTo.name?.split(' ')[0]}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )}
      </TableCell>

      {/* Created Date */}
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {format(new Date(lead.createdAt), 'M/d/yy')}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, lead)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  const renderSkeletonRows = () => (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          <TableCell colSpan={7}>
            <Skeleton height={45} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
        <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          No leads found
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {searchQuery || Object.keys(filters).length > 0
            ? 'Try adjusting your search or filters'
            : 'Get started by adding your first lead'}
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreateLead}>
          Add Lead
        </Button>
      </TableCell>
    </TableRow>
  );

  // Mobile card configuration for leads
  const mobileCardConfig = {
    primaryField: 'contactName' as keyof Lead,
    secondaryField: 'companyName' as keyof Lead,
    statusField: 'status' as keyof Lead,
    statusConfig: {
      new: { color: '#1890ff', text: 'New' },
      contacted: { color: '#722ed1', text: 'Contacted' },
      qualified: { color: '#52c41a', text: 'Qualified' },
      unqualified: { color: '#faad14', text: 'Unqualified' },
      converted: { color: '#52c41a', text: 'Converted' },
      lost: { color: '#ff4d4f', text: 'Lost' },
    },
    avatar: { icon: <PersonIcon /> },
    fields: [
      { key: 'email' as keyof Lead, label: 'Email' },
      { key: 'phone' as keyof Lead, label: 'Phone' },
      {
        key: 'priority' as keyof Lead,
        label: 'Priority',
        render: (value: string) => (
          <Chip
            label={priorityConfig[value]?.label || value}
            color={priorityConfig[value]?.color || 'default'}
            size="small"
            variant="outlined"
          />
        )
      },
      {
        key: 'aiScore' as keyof Lead,
        label: 'AI Score',
        render: (value: number) => value ? `${Math.round(value)}%` : '-'
      },
    ],
    onClick: (lead: Lead) => handleViewLead(lead),
  };

  // Mobile filters configuration
  const mobileFilters = [
    {
      key: 'status',
      placeholder: 'Status',
      value: filters.status || '',
      onChange: (value: string) => {
        setFilters({ ...filters, status: value || undefined });
        setPage(0);
      },
      options: [
        { label: 'All', value: '' },
        { label: 'New', value: 'NEW' },
        { label: 'Contacted', value: 'CONTACTED' },
        { label: 'Qualified', value: 'QUALIFIED' },
        { label: 'Unqualified', value: 'UNQUALIFIED' },
        { label: 'Converted', value: 'CONVERTED' },
        { label: 'Lost', value: 'LOST' },
      ],
    },
    {
      key: 'priority',
      placeholder: 'Priority',
      value: filters.priority || '',
      onChange: (value: string) => {
        setFilters({ ...filters, priority: value || undefined });
        setPage(0);
      },
      options: [
        { label: 'All', value: '' },
        { label: 'Urgent', value: 'URGENT' },
        { label: 'High', value: 'HIGH' },
        { label: 'Medium', value: 'MEDIUM' },
        { label: 'Low', value: 'LOW' },
      ],
    },
    {
      key: 'source',
      placeholder: 'Source',
      value: filters.source || '',
      onChange: (value: string) => {
        setFilters({ ...filters, source: value || undefined });
        setPage(0);
      },
      options: [
        { label: 'All', value: '' },
        { label: 'Web Form', value: 'WEB_FORM' },
        { label: 'Referral', value: 'REFERRAL' },
        { label: 'Event', value: 'EVENT' },
        { label: 'Cold Call', value: 'COLD_CALL' },
        { label: 'Social Media', value: 'SOCIAL_MEDIA' },
        { label: 'Partner', value: 'PARTNER' },
        { label: 'Other', value: 'OTHER' },
      ],
    },
  ];

  // Mobile View
  if (isMobile) {
    return (
      <Box sx={{ pb: 2 }}>
        {/* Mobile Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Leads
          </Typography>
          <Box display="flex" gap={1}>
            <IconButton onClick={() => refetch()} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
            <PermissionGuard permission="leads:create">
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCreateLead}
              >
                Add
              </Button>
            </PermissionGuard>
          </Box>
        </Box>

        {/* Error Alert */}
        {isError && (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load leads.
          </Alert>
        )}

        {/* Mobile List */}
        <MobileDataList
          data={leads}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={isLoading}
          emptyText="No leads found"
          emptyDescription={searchQuery || Object.keys(filters).length > 0
            ? 'Try adjusting your search or filters'
            : 'Get started by adding your first lead'}
          onRefresh={() => refetch()}
          searchPlaceholder="Search leads..."
          onSearch={(value) => {
            setSearchQuery(value);
            setPage(0);
          }}
          filters={mobileFilters}
          pagination={{
            current: page + 1,
            pageSize: rowsPerPage,
            total: totalLeads,
            onChange: (newPage) => setPage(newPage - 1),
          }}
          swipeActions={{
            left: {
              icon: <DeleteIcon />,
              color: '#ff4d4f',
              label: 'Delete',
              onClick: (lead) => handleDeleteLead(lead.id),
            },
            right: {
              icon: <EditIcon />,
              color: '#1890ff',
              label: 'Edit',
              onClick: (lead) => handleEditLead(lead),
            },
          }}
        />

        {/* Lead Form Dialog */}
        <LeadForm
          open={leadFormOpen}
          mode={leadFormMode}
          initialData={selectedLead || undefined}
          onClose={() => setLeadFormOpen(false)}
          onSubmit={handleLeadFormSubmit}
          isSubmitting={createLead.isPending || updateLead.isPending}
        />

        {/* Lead Detail Drawer */}
        <LeadDetailDrawer
          open={detailDrawerOpen}
          lead={selectedLead ? transformLeadForDrawer(selectedLead) : null}
          activities={leadActivities || []}
          assignees={assignees || []}
          onClose={() => {
            setDetailDrawerOpen(false);
            setSelectedLead(null);
          }}
          onUpdateStatus={handleUpdateStatus}
          onUpdatePriority={handleUpdatePriority}
          onAddActivity={handleAddActivity}
          onAssignLead={handleAssignLead}
          onEdit={() => selectedLead && handleEditLead(selectedLead)}
          onDelete={handleDeleteLead}
          onVerifyEmail={handleVerifyEmail}
          onSendEmail={(lead) => {
            if (!lead.email) {
              showToast.warning('No email address on this lead');
              return;
            }
            setEmailRecipient({
              name: lead.name,
              email: lead.email,
              company: lead.companyName,
              leadId: lead.id,
            });
            setEmailDialogOpen(true);
          }}
          onConvert={handleConvertLead}
          onScoreLead={handleScoreLead}
          isUpdating={updateLeadStatus.isPending || updateLeadPriority.isPending || addLeadActivity.isPending}
          isAssigning={assignLead.isPending}
          isScoring={scoreLead.isPending}
        />

        {/* Quick Task Dialog (mobile) */}
        <Dialog open={quickTaskDialogOpen} onClose={handleQuickTaskClose} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <TaskIcon fontSize="small" color="primary" />
              Create Task — {quickTaskLead?.contactName}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
              <TextField
                label="Title"
                value={quickTaskForm.title}
                onChange={(e) => setQuickTaskForm({ ...quickTaskForm, title: e.target.value })}
                fullWidth
                size="small"
                required
                autoFocus
              />
              <Stack direction="row" spacing={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={quickTaskForm.taskType}
                    label="Type"
                    onChange={(e) => setQuickTaskForm({ ...quickTaskForm, taskType: e.target.value })}
                  >
                    <MenuItem value="FOLLOW_UP">Follow Up</MenuItem>
                    <MenuItem value="CALL">Call</MenuItem>
                    <MenuItem value="EMAIL">Email</MenuItem>
                    <MenuItem value="MEETING">Meeting</MenuItem>
                    <MenuItem value="QUOTE">Quote</MenuItem>
                    <MenuItem value="DEMO">Demo</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={quickTaskForm.priority}
                    label="Priority"
                    onChange={(e) => setQuickTaskForm({ ...quickTaskForm, priority: e.target.value })}
                  >
                    <MenuItem value="URGENT">Urgent</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                label="Due Date"
                type="datetime-local"
                value={quickTaskForm.dueDate}
                onChange={(e) => setQuickTaskForm({ ...quickTaskForm, dueDate: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <FormControl size="small" fullWidth required>
                <InputLabel>Assign To</InputLabel>
                <Select
                  value={quickTaskForm.assignedToUserId}
                  label="Assign To"
                  onChange={(e) => setQuickTaskForm({ ...quickTaskForm, assignedToUserId: e.target.value })}
                >
                  {assignees?.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleQuickTaskClose} size="small">Cancel</Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleQuickTaskSubmit}
              disabled={!quickTaskForm.title.trim() || !quickTaskForm.assignedToUserId || createTask.isPending}
            >
              {createTask.isPending ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Desktop View
  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="end" alignItems="center" mb={3}>
      
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <PermissionGuard permission="leads:create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateLead}
            >
              Add Lead
            </Button>
          </PermissionGuard>
        </Box>
      </Box>

      {/* Error Alert */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load leads. Please make sure you are logged in.
          {error && <Typography variant="caption" display="block">{(error as Error).message}</Typography>}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} id="tour-leads-filters">
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value || undefined });
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="NEW">New</MenuItem>
              <MenuItem value="CONTACTED">Contacted</MenuItem>
              <MenuItem value="QUALIFIED">Qualified</MenuItem>
              <MenuItem value="UNQUALIFIED">Unqualified</MenuItem>
              <MenuItem value="CONVERTED">Converted</MenuItem>
              <MenuItem value="LOST">Lost</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority || ''}
              label="Priority"
              onChange={(e) => {
                setFilters({ ...filters, priority: e.target.value || undefined });
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="URGENT">Urgent</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={filters.source || ''}
              label="Source"
              onChange={(e) => {
                setFilters({ ...filters, source: e.target.value || undefined });
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="WEB_FORM">Web Form</MenuItem>
              <MenuItem value="REFERRAL">Referral</MenuItem>
              <MenuItem value="EVENT">Event</MenuItem>
              <MenuItem value="COLD_CALL">Cold Call</MenuItem>
              <MenuItem value="SOCIAL_MEDIA">Social Media</MenuItem>
              <MenuItem value="PARTNER">Partner</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
          {(searchQuery || Object.keys(filters).some(k => filters[k as keyof LeadFilters])) && (
            <Button
              variant="text"
              onClick={() => {
                setSearchQuery('');
                setFilters({});
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {isLoading && <LinearProgress />}
        <Table>
          {renderTableHeader()}
          <TableBody>
            {isLoading
              ? renderSkeletonRows()
              : leads.length === 0
              ? renderEmptyState()
              : leads.map(renderTableRow)}
          </TableBody>
        </Table>
        {totalLeads > 0 && (
          <TablePagination
            component="div"
            count={totalLeads}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => menuLead && handleViewLead(menuLead)}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <PermissionGuard permission="leads:update">
          <MenuItem onClick={() => menuLead && handleEditLead(menuLead)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit Lead</ListItemText>
          </MenuItem>
        </PermissionGuard>
        <MenuItem onClick={(e) => { handleCloseMenu(); menuLead && handleQuickTaskOpen(e, menuLead); }}>
          <ListItemIcon><TaskIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Create Task</ListItemText>
        </MenuItem>
        {menuLead?.phone && (
          <MenuItem onClick={() => window.open(`tel:${menuLead.phone}`)}>
            <ListItemIcon><PhoneIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Call</ListItemText>
          </MenuItem>
        )}
        {menuLead?.email && (
          <MenuItem onClick={() => {
            setEmailRecipient({
              name: menuLead.contactName,
              email: menuLead.email,
              company: menuLead.companyName,
              leadId: menuLead.id,
            });
            setEmailDialogOpen(true);
            setAnchorEl(null);
          }}>
            <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Send Email</ListItemText>
          </MenuItem>
        )}
        {menuLead?.status !== 'CONVERTED' && (
          <>
            <Divider />
            <MenuItem onClick={() => menuLead && handleConvertLead(menuLead.id)} sx={{ color: 'success.main' }}>
              <ListItemIcon><ConvertIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText>Convert to Client</ListItemText>
            </MenuItem>
          </>
        )}
        <Divider />
        <PermissionGuard permission="leads:delete">
          <MenuItem onClick={() => menuLead && handleDeleteLead(menuLead.id)} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </PermissionGuard>
      </Menu>

      {/* Lead Form Dialog */}
      <LeadForm
        open={leadFormOpen}
        mode={leadFormMode}
        initialData={selectedLead || undefined}
        onClose={() => setLeadFormOpen(false)}
        onSubmit={handleLeadFormSubmit}
        isSubmitting={createLead.isPending || updateLead.isPending}
      />

      {/* Lead Detail Drawer */}
      <LeadDetailDrawer
        open={detailDrawerOpen}
        lead={selectedLead ? transformLeadForDrawer(selectedLead) : null}
        activities={leadActivities || []}
        assignees={assignees || []}
        onClose={() => {
          setDetailDrawerOpen(false);
          setSelectedLead(null);
        }}
        onUpdateStatus={handleUpdateStatus}
        onUpdatePriority={handleUpdatePriority}
        onAddActivity={handleAddActivity}
        onAssignLead={handleAssignLead}
        onEdit={() => selectedLead && handleEditLead(selectedLead)}
        onDelete={handleDeleteLead}
        onVerifyEmail={handleVerifyEmail}
        onConvert={handleConvertLead}
        onSendEmail={(lead) => {
          setEmailRecipient({ name: lead.name, email: lead.email, company: lead.companyName, leadId: lead.id });
          setEmailDialogOpen(true);
        }}
        onScoreLead={handleScoreLead}
        isUpdating={updateLeadStatus.isPending || updateLeadPriority.isPending || addLeadActivity.isPending}
        isAssigning={assignLead.isPending}
        isScoring={scoreLead.isPending}
      />

      {/* Compose Email Dialog */}
      <ComposeEmailDialog
        open={emailDialogOpen}
        onClose={() => {
          setEmailDialogOpen(false);
          setEmailRecipient(null);
        }}
        onSuccess={() => {
          // Refresh activities for the current lead
          if (emailRecipient?.leadId) {
            queryClient.invalidateQueries({ queryKey: ['ai-crm', 'leads', emailRecipient.leadId, 'activities'] });
          }
        }}
        recipientName={emailRecipient?.name}
        recipientEmail={emailRecipient?.email}
        recipientCompany={emailRecipient?.company}
        leadId={emailRecipient?.leadId}
      />

      {/* Quick Note Popover */}
      <Popover
        open={Boolean(notePopoverAnchor)}
        anchorEl={notePopoverAnchor}
        onClose={handleQuickNoteClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, width: 300 } }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Add Note — {notePopoverLead?.contactName}
        </Typography>
        <TextField
          autoFocus
          multiline
          rows={3}
          fullWidth
          size="small"
          placeholder="Type your note..."
          value={quickNoteText}
          onChange={(e) => setQuickNoteText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleQuickNoteSubmit();
          }}
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button size="small" onClick={handleQuickNoteClose}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleQuickNoteSubmit}
            disabled={!quickNoteText.trim() || addLeadActivity.isPending}
          >
            Save
          </Button>
        </Stack>
      </Popover>

      {/* Quick Task Dialog */}
      <Dialog open={quickTaskDialogOpen} onClose={handleQuickTaskClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <TaskIcon fontSize="small" color="primary" />
            Create Task — {quickTaskLead?.contactName}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
            <TextField
              label="Title"
              value={quickTaskForm.title}
              onChange={(e) => setQuickTaskForm({ ...quickTaskForm, title: e.target.value })}
              fullWidth
              size="small"
              required
              autoFocus
            />
            <Stack direction="row" spacing={1}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={quickTaskForm.taskType}
                  label="Type"
                  onChange={(e) => setQuickTaskForm({ ...quickTaskForm, taskType: e.target.value })}
                >
                  <MenuItem value="FOLLOW_UP">Follow Up</MenuItem>
                  <MenuItem value="CALL">Call</MenuItem>
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="MEETING">Meeting</MenuItem>
                  <MenuItem value="QUOTE">Quote</MenuItem>
                  <MenuItem value="DEMO">Demo</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={quickTaskForm.priority}
                  label="Priority"
                  onChange={(e) => setQuickTaskForm({ ...quickTaskForm, priority: e.target.value })}
                >
                  <MenuItem value="URGENT">Urgent</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="LOW">Low</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField
              label="Due Date"
              type="datetime-local"
              value={quickTaskForm.dueDate}
              onChange={(e) => setQuickTaskForm({ ...quickTaskForm, dueDate: e.target.value })}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <FormControl size="small" fullWidth required>
              <InputLabel>Assign To</InputLabel>
              <Select
                value={quickTaskForm.assignedToUserId}
                label="Assign To"
                onChange={(e) => setQuickTaskForm({ ...quickTaskForm, assignedToUserId: e.target.value })}
              >
                {assignees?.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleQuickTaskClose} size="small">Cancel</Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleQuickTaskSubmit}
            disabled={!quickTaskForm.title.trim() || !quickTaskForm.assignedToUserId || createTask.isPending}
          >
            {createTask.isPending ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attribute Selection Dialog (shown before creating a new lead) */}
      <AttributeSelectionDialog
        open={attrDialogOpen}
        onClose={() => {
          setAttrDialogOpen(false);
          setPendingLeadData(null);
        }}
        onConfirm={handleConfirmCreateLead}
        loading={attrDialogLoading}
        entityName={pendingLeadData?.companyName || pendingLeadData?.name}
        appliesTo="LEAD"
      />
    </Box>
  );
};

export default LeadsTab;
